import React from "react";
import { Link } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import { useLang } from "@/context/LangContext";
import { waLink } from "@/lib/wa";
import PageHero from "@/components/public/PageHero";
import Reveal from "@/components/public/Reveal";
import { Mail, Phone, MessageCircle, MapPin, Clock, Warehouse } from "lucide-react";

export default function Contact() {
  const { settings } = useSettings();
  const { t } = useLang();
  const rows = [
    { icon: Mail, label: "Email", value: settings.email, href: settings.email ? `mailto:${settings.email}` : null },
    { icon: Phone, label: "Phone", value: settings.phone, href: settings.phone ? `tel:${settings.phone}` : null },
    { icon: MessageCircle, label: "WhatsApp", value: settings.whatsapp, href: waLink(settings.whatsapp) },
    { icon: MapPin, label: "Address", value: settings.address },
    { icon: Warehouse, label: "Warehouse Location", value: settings.warehouse_address },
    { icon: Clock, label: "Business Hours", value: settings.business_hours },
  ];
  return (
    <div>
      <PageHero eyebrow="Get in Touch" title="Contact" sub="Warehouse Coffee — Indonesian Arabica Green Coffee. Reach our team for availability, specifications and quotations." />
      <section className="mx-auto max-w-[1400px] px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="space-y-4">
              {rows.map((r) => (
                <div key={r.label} className="flex items-start gap-4 rounded-xl border border-forest/10 bg-white p-5">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-beige text-forest"><r.icon className="h-5 w-5" /></div>
                  <div className="min-w-0">
                    <div className="font-mono text-[11px] uppercase tracking-wider text-espresso/50">{r.label}</div>
                    {r.value ? (
                      r.href ? <a href={r.href} target="_blank" rel="noreferrer" className="mt-0.5 block break-words font-body text-sm font-medium text-forest-dark hover:text-amber-brand">{r.value}</a>
                      : <div className="mt-0.5 break-words font-body text-sm font-medium text-forest-dark">{r.value}</div>
                    ) : <div className="mt-0.5 font-body text-sm italic text-espresso/40">To be updated</div>}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="rounded-2xl bg-forest p-8 lg:p-10">
              <h2 className="font-display text-2xl font-extrabold text-offwhite">Ready to source Indonesian green coffee?</h2>
              <p className="mt-3 font-body text-sm leading-relaxed text-offwhite/70">Send us your requirements and our team will respond with availability and commercial details.</p>
              <div className="mt-8 flex flex-col gap-3">
                <Link to="/request-quote" data-testid="contact-quote-cta" className="rounded-full bg-amber-brand px-6 py-4 text-center font-display text-sm font-bold uppercase tracking-wider text-espresso-dark transition-all hover:bg-amber-soft">{t("cta.quote")}</Link>
                <a href={waLink(settings.whatsapp)} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-cta" className="flex items-center justify-center gap-2 rounded-full border border-offwhite/30 px-6 py-4 font-display text-sm font-bold uppercase tracking-wider text-offwhite transition-all hover:bg-offwhite/10"><MessageCircle className="h-4 w-4" /> {t("cta.whatsapp")}</a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
